import { useQueryClient } from '@tanstack/react-query';
import { useId, useRef, useState, type RefObject } from 'react';
import { actions } from '@/lib/actions';
import { api, ApiError, errorMessage } from '@/lib/api';
import { useMe, useTeam } from '@/lib/hooks';
import { BROWSER_TZ } from '@/lib/timezone';
import { toast, toastError } from '@/lib/toast';
import { Segmented } from '@/ui/bits';
import { dueBody, startDue, type Due } from '@/ui/due';
import { DueInput } from '@/ui/DueInput';
import { Icon } from '@/ui/Icon';
import type { IconName } from '@/ui/icons';
import { CALL_OUTCOMES, firstName, type CallOutcome, type NoteItem, type RecordSheet } from '@shared/record';
import type { FollowupItem } from '@shared/types';
import { announceComment, usePostComment } from './Comments';
import { MentionInput } from './MentionInput';
import { dropTimeline, keys, prependTimeline } from './queries';
import { DURATIONS, markFresh } from './Timeline';
import { startUploads } from './upload';
import { shake } from './util';

// The box at the top of the record's timeline: a note, a call, an email, a comment for the team, or a follow-up.

export type ComposerMode = 'note' | 'call' | 'email' | 'comment' | 'fu';

const MODES: { id: ComposerMode; icon: IconName; label: string }[] = [
  { id: 'note', icon: 'note', label: 'Note' },
  { id: 'call', icon: 'phone', label: 'Call' },
  { id: 'email', icon: 'mail', label: 'Email' },
  { id: 'comment', icon: 'comment', label: 'Comment' },
  { id: 'fu', icon: 'clock', label: 'Follow-up' },
];

/** Opens the email composer addressed to this person, or about this company. */
export function emailRecord(sheet: RecordSheet): void {
  if (sheet.type === 'person') {
    const main = sheet.emails[0]?.email;
    actions.compose({
      to: main ? [{ personId: sheet.id, email: main, name: sheet.name }] : [],
      personId: sheet.id,
      companyId: sheet.company?.id,
    });
  } else {
    actions.compose({ companyId: sheet.id });
  }
}

export function Composer({
  sheet,
  mode,
  onMode,
  inputRef,
  value,
  setValue,
  canAttach,
}: {
  sheet: RecordSheet;
  mode: ComposerMode;
  onMode: (mode: ComposerMode) => void;
  inputRef: RefObject<HTMLTextAreaElement | null>;
  value: string;
  setValue: (value: string) => void;
  canAttach: boolean;
}) {
  const qc = useQueryClient();
  const me = useMe();
  const { members, active } = useTeam();
  const postComment = usePostComment(sheet.type, sheet.id);
  const [minutes, setMinutes] = useState(15);
  const [outcome, setOutcome] = useState<CallOutcome>('connected');
  const [due, setDue] = useState<Due>(() => startDue(new Date(), BROWSER_TZ, 'monday'));
  const [owner, setOwner] = useState(me.user.id);
  const [error, setError] = useState<string | null>(null);
  const [dueMissing, setDueMissing] = useState(false);
  const [busy, setBusy] = useState(false);
  const fileRef = useRef<HTMLInputElement>(null);
  const errId = useId();
  const name = sheet.type === 'person' ? firstName(sheet.name) : sheet.name;
  const link = sheet.type === 'person' ? { personId: sheet.id } : { companyId: sheet.id };

  const placeholder = {
    note: `Add a note about ${name}…`,
    call: 'What did you talk about?',
    email: '',
    comment: 'Write a comment…',
    fu: 'What needs to happen next?',
  }[mode];

  const fail = (err: unknown, body: string) => {
    setValue(body);
    setError(err instanceof ApiError ? (err.fields.body ?? err.fields.title ?? Object.values(err.fields)[0] ?? err.message) : errorMessage(err));
  };

  const postNote = async (body: string) => {
    const kind = mode === 'call' ? 'call' : 'note';
    const now = new Date().toISOString();
    const tempId = `tmp-${crypto.randomUUID()}`;
    const temp: NoteItem = {
      id: tempId,
      kind,
      body,
      callOutcome: kind === 'call' ? outcome : null,
      callDurationMin: kind === 'call' ? minutes : null,
      callDirection: kind === 'call' ? 'out' : null,
      occurredAt: now,
      author: { id: me.user.id, name: me.user.name, hue: me.user.hue },
      person: sheet.type === 'person' ? { id: sheet.id, name: sheet.name } : null,
      company: sheet.type === 'company' ? { id: sheet.id, name: sheet.name } : sheet.company,
      deal: null,
      createdAt: now,
      updatedAt: now,
    };
    prependTimeline(qc, sheet.type, sheet.id, { kind: 'note', id: `note:${tempId}`, at: now, note: temp });
    try {
      const saved = await api.post<NoteItem>('/notes', {
        kind,
        body,
        ...link,
        ...(kind === 'call' ? { callOutcome: outcome, callDurationMin: minutes, callDirection: 'out' } : {}),
      });
      dropTimeline(qc, sheet.type, sheet.id, `note:${tempId}`);
      prependTimeline(qc, sheet.type, sheet.id, { kind: 'note', id: `note:${saved.id}`, at: saved.occurredAt, note: saved });
      markFresh(`note:${saved.id}`);
      void qc.invalidateQueries({ queryKey: keys.record(sheet.type, sheet.id) });
      toast(kind === 'call' ? 'Call logged' : 'Note saved', {
        icon: 'check-circle',
        undo: () => {
          api
            .post('/bin', { type: 'note', ids: [saved.id] })
            .then(() => qc.invalidateQueries({ queryKey: keys.record(sheet.type, sheet.id) }))
            .catch((err) => toastError(err));
        },
      });
    } catch (err) {
      dropTimeline(qc, sheet.type, sheet.id, `note:${tempId}`);
      throw err;
    }
  };

  const postFollowup = async (title: string) => {
    const saved = await api.post<FollowupItem>('/followups', {
      title,
      ...dueBody(due, BROWSER_TZ),
      ownerId: owner,
      ...link,
    });
    void qc.invalidateQueries({ queryKey: keys.followups(sheet.type, sheet.id) });
    void qc.invalidateQueries({ queryKey: keys.record(sheet.type, sheet.id) });
    const who = owner === me.user.id ? '' : ` for ${firstName(members.find((m) => m.id === owner)?.name ?? '')}`;
    toast(`Follow-up added${who}`, {
      icon: 'check-circle',
      undo: () => {
        api
          .post('/bin', { type: 'followup', ids: [saved.id] })
          .then(() => qc.invalidateQueries({ queryKey: ['followups'] }))
          .catch((err) => toastError(err));
      },
    });
  };

  const submit = async () => {
    if (busy) return;
    const body = value.trim();
    if (!body) {
      shake(inputRef.current);
      inputRef.current?.focus();
      return;
    }
    if (mode === 'fu' && !due.date) {
      setDueMissing(true);
      return;
    }
    setBusy(true);
    setError(null);
    setValue('');
    try {
      if (mode === 'comment') {
        const saved = await postComment(body);
        void qc.invalidateQueries({ queryKey: keys.timeline(sheet.type, sheet.id) });
        void qc.invalidateQueries({ queryKey: keys.sheet(sheet.type, sheet.id) });
        markFresh(`comment:${saved.id}`);
        announceComment(saved, members, me.user.id, () => void qc.invalidateQueries({ queryKey: keys.record(sheet.type, sheet.id) }));
      } else if (mode === 'fu') {
        await postFollowup(body);
      } else {
        await postNote(body);
      }
    } catch (err) {
      fail(err, body);
    } finally {
      setBusy(false);
    }
  };

  const pickMode = (m: ComposerMode) => {
    if (m === 'email') {
      emailRecord(sheet);
      return;
    }
    onMode(m);
    setError(null);
    requestAnimationFrame(() => inputRef.current?.focus());
  };

  const label = { note: 'Save note', call: 'Log call', email: '', comment: 'Post comment', fu: 'Add follow-up' }[mode];
  return (
    <div className="rcomp" id={`rcomp-${sheet.id}`}>
      <Segmented className="modes" label="Add to timeline" options={MODES} value={mode} onChange={pickMode} />
      <MentionInput
        value={value}
        onChange={(v) => {
          setValue(v);
          if (error) setError(null);
        }}
        onSubmit={() => void submit()}
        placeholder={placeholder}
        inputRef={inputRef}
        mentions={mode === 'comment'}
        invalid={!!error}
        describedBy={error ? errId : undefined}
      />
      {error ? (
        <p className="field-err" id={errId} role="alert">
          <Icon name="warn" className="i-xs" />
          {error}
        </p>
      ) : null}
      {mode === 'fu' && dueMissing ? (
        <p className="field-err" id={`${errId}-due`} role="alert">
          <Icon name="warn" className="i-xs" />
          Choose a due date
        </p>
      ) : null}
      <div className="extras">
        {mode === 'note' && canAttach ? (
          <>
            <button type="button" className="btn btn-sm btn-ghost" onClick={() => fileRef.current?.click()}>
              <Icon name="clip" />
              Attach
            </button>
            <input
              ref={fileRef}
              type="file"
              multiple
              hidden
              aria-hidden="true"
              tabIndex={-1}
              onChange={(e) => {
                if (e.target.files?.length) {
                  startUploads(qc, sheet.type, sheet.id, e.target.files);
                  toast(`Uploading to ${name}'s files`, { icon: 'upload' });
                }
                e.target.value = '';
              }}
            />
          </>
        ) : null}
        {mode === 'call' ? (
          <>
            <select className="select select-sm" aria-label="Duration" value={minutes} onChange={(e) => setMinutes(Number(e.target.value))}>
              {DURATIONS.map((m) => (
                <option key={m} value={m}>
                  {m} min
                </option>
              ))}
            </select>
            <select className="select select-sm" aria-label="Outcome" value={outcome} onChange={(e) => setOutcome(e.target.value as CallOutcome)}>
              {CALL_OUTCOMES.map((o) => (
                <option key={o.id} value={o.id}>
                  {o.label}
                </option>
              ))}
            </select>
          </>
        ) : null}
        {mode === 'comment' ? (
          <span className="hint">
            <Icon name="lock" />
            Only the team sees comments
          </span>
        ) : null}
        {mode === 'fu' ? (
          <>
            <DueInput
              compact
              value={due}
              invalid={dueMissing}
              describedBy={dueMissing ? `${errId}-due` : undefined}
              onChange={(next) => {
                setDue(next);
                setDueMissing(false);
              }}
            />
            <select className="select select-sm" aria-label="Assignee" value={owner} onChange={(e) => setOwner(e.target.value)}>
              {active.map((m) => (
                <option key={m.id} value={m.id}>
                  {m.id === me.user.id ? `${firstName(m.name)} (you)` : firstName(m.name)}
                </option>
              ))}
            </select>
          </>
        ) : null}
        <span className="spacer" />
        <button type="button" className="btn btn-sm btn-primary" onClick={() => void submit()} disabled={busy}>
          {label}
        </button>
      </div>
    </div>
  );
}
